"use client";

import { useState } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { followArtist, unfollowArtist, getFollowStatus } from "@/features/artists/api";
import { useAuthStore } from "@/store/useAuthStore";
import { toastApiError } from "@/lib/api-toast";
export type FollowState = "FOLLOWED" | "NOT_FOLLOWED";

/** The follow behaviour, shared by the legacy and v2 buttons so the two
 *  appearances can never disagree about what a click does. */
export function useFollowToggle({
  username,
  initialState,
  isFa,
  fromList = false,
  onChange
}: {
  username: string;
  initialState?: FollowState;
  isFa: boolean;
  /** Told the new state once the server took it — a list updates its own card (state and count). */
  onChange?: (state: FollowState) => void;
  /** The state comes with a list that is still being read under the viewer's session: wait for it, never ask per card. */
  fromList?: boolean;
}) {
  const {
    user,
    openLoginModal
  } = useAuthStore();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Owned state: seeded from the server-provided initialState (or the status
  // query below), then updated locally on success. This is what lets the
  // button persist its flip without a router.refresh() of the whole page —
  // the old useOptimistic approach reverted to the stale server prop as soon
  // as the transition ended, which forced a full server re-render to fix.
  const [override, setOverride] = useState<FollowState | null>(null);
  const {
    data: fetchedState,
    isLoading: isQueryLoading
  } = useQuery({
    queryKey: ["followStatus", username],
    queryFn: async () => {
      const res = await getFollowStatus(username);
      return res.data.state;
    },
    enabled: !!user && !initialState && !fromList,
    staleTime: 5 * 60 * 1000
  });
  const currentState = override ?? initialState ?? fetchedState ?? "NOT_FOLLOWED";
  const toggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      openLoginModal();
      return;
    }
    if (isSubmitting) return;
    const previousState = currentState;
    const newState = currentState === "FOLLOWED" ? "NOT_FOLLOWED" : "FOLLOWED";
    setIsSubmitting(true);
    setOverride(newState);
    try {
      if (newState === "FOLLOWED") {
        await followArtist(username);
        toast.success(isFa ? "کاربر دنبال شد" : "User followed");
      } else {
        await unfollowArtist(username);
        toast.success(isFa ? "کاربر آنفالو شد" : "User unfollowed");
      }
      queryClient.invalidateQueries({
        queryKey: ["followStatus", username]
      });
      onChange?.(newState);
    } catch (error) {
      setOverride(previousState);
      toastApiError(error, isFa ? "خطا در انجام عملیات" : "Operation failed");
      console.error("Follow toggle error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const isFollowed = currentState === "FOLLOWED";
  const isFetching = !!user && !initialState && (fromList || isQueryLoading);
  return {
    isFollowed,
    busy: isSubmitting || isFetching,
    toggle
  };
}